"use client";

import Link from "next/link";
import type { CatalogPiece } from "@/lib/api/api-client";
import { pieceTierLine } from "@/lib/library/difficulty-tiers";
import { libraryPiecePath } from "@/lib/library/library-routes";
import { pieceRightsMark } from "@/lib/library/piece-rights";
import { pieceDisplayArtist } from "@/lib/library/piece-format";
import { PieceSilhouette } from "./piece-silhouette";

/**
 * Une carte de morceau : la silhouette en tuile, le titre et le compositeur
 * posés dessus, et sous la tuile une ligne qui dit le geste et le chantier.
 *
 * # Pourquoi le titre est *par-dessus* la figure
 *
 * C'est le point de la mise en page, et il tient au fait que les silhouettes
 * sont toutes différentes. Empilées en grille — figure en haut, texte en bas —
 * vingt-quatre figures dissemblables ne font pas une grille : l'œil n'a aucun
 * point fixe d'une carte à l'autre et l'ensemble se lit comme du bruit.
 *
 * Le bloc de texte, lui, est au même endroit sur toutes les cartes. Posé sur la
 * tuile, il devient l'ancre constante que la grille partage, et la variabilité
 * de la silhouette redevient ce qu'elle doit être — de quoi reconnaître un
 * morceau — au lieu d'un désordre.
 *
 * # Ce qui a disparu de la carte
 *
 * **Les pastilles de notoriété.** La notoriété anti-corrèle avec
 * l'accessibilité : la montrer sur chaque carte pousse vers les morceaux les
 * plus connus, qui sont en moyenne les moins jouables. Elle devient de la
 * curation — une sélection « Des mélodies que tu connais », où c'est le serveur qui
 * a déjà croisé les deux critères.
 *
 * **Le niveau chiffré et sa jauge.** Remplacés par une ligne en toutes lettres
 * (« Une main pose · Une soirée ») : voir `lib/library/difficulty-tiers.ts`.
 *
 * # Un lien, pas un bouton
 *
 * La carte mène à une URL (`/library/<slug>`). En faire un `<button>`, c'est
 * perdre l'ouverture dans un nouvel onglet, l'aperçu de la destination dans la
 * barre d'état, et le clic milieu — pour ne rien gagner.
 */

/**
 * La tuile, en unités de dessin. Elles donnent aussi ses proportions au
 * conteneur, pour que la silhouette ne soit jamais étirée.
 */
const TILE_WIDTH = 200;
const TILE_HEIGHT = 150;

/** Part de la tuile couverte par le voile. Voir le raisonnement au rendu. */
const VEIL_HEIGHT_RATIO = 0.45;

type Props = {
  piece: CatalogPiece;
  /** Remonté avant la navigation, pour la mesure des sélections. */
  onOpen?: () => void;
  /**
   * Le bouton d'actions, posé en haut à droite **de la tuile**.
   *
   * Hors du lien, donc : un bouton dans un lien est un lien qu'on ne peut plus
   * ouvrir dans un onglet sans tomber sur le bouton, et un bouton qu'un clic
   * milieu déclenche. Il est toujours visible, jamais révélé au survol — il n'y
   * a pas de survol au doigt, et une action qu'on ne découvre qu'à la souris
   * n'existe pas sur la moitié des écrans.
   *
   * Sur la tuile et non sous elle : à 200 px de large, la ligne de palier est
   * déjà pleine.
   */
  action?: React.ReactNode;
  /**
   * La deuxième ligne, sous la ligne de palier : l'état de travail, l'origine,
   * la dernière ouverture. Vide sur une carte de catalogue, où l'on ne possède
   * pas encore le morceau.
   */
  footer?: React.ReactNode;
  /**
   * Montre le signet « Dans mes morceaux » quand la pièce y est déjà.
   *
   * Réservé aux surfaces du **catalogue** : sur « Mes morceaux », tout y est,
   * et vingt signets identiques n'apprennent rien. Le signet se pose sur la
   * figure et non sur un aplat, comme la marque « Draft ».
   */
  bookmark?: boolean;
  /**
   * Ce que la pastille du coin dit, quand il y a quelque chose à dire.
   *
   * **`rights`** — les droits du morceau : « Sous droits », « Droits non
   * établis », ou la licence quand elle tient. C'est ce qui parle sur les
   * surfaces de bibliothèque, où le morceau est à soi et où la question est
   * « qu'ai-je le droit d'en faire ». Rien sur le domaine public, qui est la
   * règle de tout le catalogue.
   *
   * **`publication`** — « Non publié », c'est-à-dire l'état d'éditeur. Il n'a
   * de sens que sur les surfaces de catalogue en prévisualisation admin
   * (`?drafts=1`), où l'on est précisément en train de juger ce qui doit
   * paraître.
   *
   * Un seul emplacement et un seul style dans les deux cas : la carte ne se
   * charge pas, elle change de mot.
   */
  marking?: "rights" | "publication";
};

export function PieceCard({ piece, onOpen, action, footer, bookmark = false, marking = "rights" }: Props) {
  const tierLine = pieceTierLine(piece.difficulty, piece.noteCount, piece.durationSec);
  /*
    Un seul des deux mots peut paraître, et lequel dépend de la surface. Décidé
    ici plutôt que par un nœud passé en prop : la position, le style et la
    cohabitation avec le bouton d'actions sont des affaires de carte.
  */
  const mark =
    marking === "rights"
      ? pieceRightsMark(piece)
      : piece.isPublic
        ? null
        : {
            label: "Non publié",
            title: "Ce morceau n'est pas au catalogue public. Tu le vois en prévisualisation.",
          };

  return (
    /*
      `h-full` : les cellules d'une rangée de grille sont étirées à la plus
      haute, mais la carte gardait sa hauteur naturelle. Un titre sur deux
      lignes suffisait alors à faire dépasser une carte de sa voisine, et le bas
      de chaque rangée devenait irrégulier — vu en rendu réel, pas au calcul. Le
      bloc de texte absorbe la différence.

      Le cadre est un `div` et non le lien lui-même depuis que la carte porte un
      bouton d'actions : celui-ci doit être *dans* la boîte pour se poser sur la
      tuile, et *hors* du lien pour rester un bouton.
    */
    <div className="library-card relative flex h-full flex-col overflow-hidden rounded-xl">
      <Link
        href={libraryPiecePath(piece)}
        onClick={onOpen}
        className="flex flex-1 flex-col rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
      >
        <div
          className="relative w-full"
          style={{
            aspectRatio: `${TILE_WIDTH} / ${TILE_HEIGHT}`,
            borderBottom: "1px solid var(--color-border-subtle)",
          }}
        >
          <PieceSilhouette
            silhouette={piece.silhouette}
            width={TILE_WIDTH}
            height={TILE_HEIGHT}
            className="absolute inset-0 block h-full w-full"
          />

          {/*
            La figure est assombrie d'environ 15 % pour que le texte passe. Un
            aplat du fond de la figure plutôt qu'un `filter: brightness` : le
            résultat est le même sur les cases colorées (elles se rapprochent du
            fond), il ne touche pas au fond lui-même, et il se compose dans la
            même passe que le voile au lieu de créer un calque par carte.
          */}
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(12, 12, 24, 0.15)" }} />

          {/*
            Le voile, et pourquoi il s'arrête à 45 %.

            Il faut bien couvrir le bas de la figure pour que le texte tienne —
            mais **le bas de la figure, c'est la main gauche**. Sur le Prélude
            op. 28 n° 4 de Chopin, la bande turquoise continue qui dit « la
            gauche tient, la droite chante » vit précisément là. Un voile plus
            haut l'effacerait, et la carte perdrait ce qu'elle est censée
            montrer.

            D'où l'arbitrage : 45 % de hauteur, et toute la marge de manœuvre prise
            *dans* cette hauteur plutôt qu'au-delà. Les paliers ci-dessous sont
            calés sur la mesure : un titre sur deux lignes monte à ~65 px du bas
            d'une tuile de 156 px, soit 93 % du voile. Le fondu doit donc rester
            soutenu presque jusqu'à son bord haut — d'où une courbe qui ne lâche
            qu'à la toute fin, complétée par l'ombre portée du texte.
          */}
          <div
            className="absolute inset-x-0 bottom-0"
            style={{
              height: `${VEIL_HEIGHT_RATIO * 100}%`,
              background:
                "linear-gradient(to top, #0c0c18 0%, #0c0c18 55%, rgba(12, 12, 24, 0.9) 78%, rgba(12, 12, 24, 0.6) 92%, rgba(12, 12, 24, 0) 100%)",
            }}
          />

          {/*
            L'ombre portée que le voile ci-dessus dit compléter. Elle était
            décrite dans le commentaire et absente du code : un titre sur deux
            lignes monte à 64 px, soit 90 % de la hauteur du voile, là où celui-ci
            n'est plus qu'à 0,64 d'alpha au-dessus d'une silhouette colorée.
          */}
          <div
            className="absolute inset-x-0 bottom-0 flex flex-col px-3 pb-2.5"
            style={{ textShadow: "0 1px 3px rgba(12, 12, 24, 0.9)" }}
          >
            {/*
              `leading-tight` et non `leading-snug` : sur deux lignes, l'interligne
              plus serré fait tenir le bloc entier dans la zone opaque du voile.
              Mesuré, pas estimé — 15 px × 1,25 × 2 lignes + le compositeur + le
              padding tombent sous les 45 % de 150 px.
            */}
            <span className="text-[15px] font-medium text-text-primary leading-tight line-clamp-2">{piece.title}</span>
            <span className="text-[12px] text-text-secondary truncate leading-snug">{pieceDisplayArtist(piece)}</span>
          </div>

          {/*
            Le signet : « ce morceau est déjà chez toi ». En haut à **gauche**,
            là où la marque de brouillon n'est pas, pour que les deux puissent
            coexister sur la même tuile en prévisualisation admin. Sur la figure
            et non sur un aplat, comme elle.

            Il porte un texte pour les lecteurs d'écran : un signet muet ne dit
            rien à qui ne le voit pas.
          */}
          {bookmark && piece.inLibrary && (
            <span
              className="absolute top-2 left-2 grid h-[22px] w-[22px] place-items-center rounded-md text-accent-bright"
              style={{ backgroundColor: "rgba(12, 12, 24, 0.62)" }}
            >
              <BookmarkGlyph />
              <span className="sr-only">Dans mes morceaux</span>
            </span>
          )}

          {/*
            La pastille de coin. Fond opaque : elle se pose sur la figure, pas
            sur un aplat.

            Elle se décale quand la carte porte un bouton d'actions, qui occupe
            le même coin. Le cas n'est pas rare : tous les imports portent une
            marque de droits, donc toute la section « Mes imports » l'affiche.

            `title` porte la conséquence en une phrase, et sert de nom
            accessible : « Sous droits » seul ne dit pas ce que ça change.
          */}
          {mark !== null && (
            <span
              title={mark.title}
              aria-label={mark.title}
              className={`absolute top-2 ${action === undefined ? "right-2" : "right-[46px]"} max-w-[62%] truncate rounded px-1.5 py-[2px] text-[9px] font-medium tracking-[0.02em] text-text-secondary`}
              style={{
                border: "1px solid var(--color-border-strong)",
                backgroundColor: "rgba(12, 12, 24, 0.72)",
              }}
            >
              {mark.label}
            </span>
          )}
        </div>

        {/*
          `mt-auto` colle cette ligne au bas de la carte, et la hauteur est
          réservée même sans donnée. Les deux servent la même chose : que le
          geste et le chantier se lisent sur une seule ligne d'œil d'un bout à
          l'autre d'une rangée.
        */}
        <span
          className={`library-card-tier mt-auto block truncate px-3 text-[11.5px] leading-4 text-text-soft ${
            footer === undefined ? "min-h-[32px] py-2" : "min-h-[26px] pt-2"
          }`}
        >
          {tierLine ?? ""}
        </span>

        {/*
          La ligne d'entrée est *dans* le lien : elle décrit le morceau, donc
          cliquer dessus doit l'ouvrir comme le reste de la carte. Sa hauteur
          est réservée même quand elle est vide — sinon une carte sans état de
          travail remonte de vingt pixels et le bas de la rangée se casse.

          Rien du tout sur une carte de catalogue : le morceau n'est pas à soi,
          il n'a ni origine ni dernière ouverture, et réserver la place d'une
          ligne qui n'arrivera jamais allongerait les vingt-quatre cartes de
          l'accueil pour rien.
        */}
        {footer !== undefined && <span className="library-card-entry block min-h-[26px] px-3 pb-2.5">{footer}</span>}
      </Link>

      {action !== undefined && <div className="absolute right-0 top-0 z-10">{action}</div>}
    </div>
  );
}

/** Un marque-page plein : « c'est rangé », pas « c'est en favori ». */
function BookmarkGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="currentColor" aria-hidden="true">
      <path d="M2.6 1h5.8a.6.6 0 0 1 .6.6v8.05a.35.35 0 0 1-.55.29L5.5 7.85l-2.95 2.09A.35.35 0 0 1 2 9.65V1.6a.6.6 0 0 1 .6-.6Z" />
    </svg>
  );
}

/**
 * L'attente d'une carte. Un squelette et non « Chargement… » : il occupe la
 * place que le contenu prendra, donc la grille ne saute pas quand les données
 * arrivent — et il dit combien de cartes viennent.
 */
export function PieceCardSkeleton() {
  return (
    <div
      className="flex flex-col overflow-hidden rounded-xl animate-pulse motion-reduce:animate-none"
      style={{
        backgroundColor: "var(--color-surface-card)",
        border: "1px solid var(--color-border-subtle)",
      }}
      aria-hidden="true"
    >
      {/*
        Le squelette suit la tuile : le titre étant désormais *dans* la figure,
        ses traits sont posés là où le texte apparaîtra, sinon la grille saute
        d'une soixantaine de pixels au moment où les données arrivent.
      */}
      <div
        className="relative w-full bg-white/[0.03]"
        style={{
          aspectRatio: `${TILE_WIDTH} / ${TILE_HEIGHT}`,
          borderBottom: "1px solid var(--color-border-subtle)",
        }}
      >
        <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 px-3 pb-3">
          <div className="h-[10px] w-3/4 rounded bg-white/[0.06]" />
          <div className="h-[9px] w-1/2 rounded bg-white/[0.04]" />
        </div>
      </div>
      <div className="px-3 py-2 min-h-[32px]">
        <div className="h-[9px] w-2/3 rounded bg-white/[0.04]" />
      </div>
    </div>
  );
}
